import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { countries, CountryCode, useCountryStore } from '@/store/countryStore';
import { MapPin } from 'lucide-react';

interface CountrySelectorProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export const CountrySelector = ({ open: controlledOpen, onOpenChange, trigger }: CountrySelectorProps) => {
  const location = useLocation();
  const { selectedCountry, setCountry } = useCountryStore();
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = isControlled ? onOpenChange! : setInternalOpen;
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Show modal on first visit if no country selected, but never above admin pages
  useEffect(() => {
    if (!selectedCountry && !isControlled && !isAdminRoute) {
      setInternalOpen(true);
      return;
    }

    if (isAdminRoute && internalOpen) {
      setInternalOpen(false);
    }
  }, [selectedCountry, isControlled, isAdminRoute, internalOpen]);

  const handleSelectCountry = (code: CountryCode) => {
    setCountry(code);
    setIsOpen(false);
  };

  return (
    <>
      {trigger && (
        <div onClick={() => setIsOpen(true)}>
          {trigger}
        </div>
      )}
      <Dialog open={isOpen && !isAdminRoute} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] overflow-hidden rounded-[1.75rem] border-border/70 bg-card p-0 shadow-2xl sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 bg-primary px-6 pb-5 pt-7 text-right text-primary-foreground">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10">
                <MapPin className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-lg font-black">اختر موقعك</span>
                <span className="mt-1 block text-xs font-medium text-primary-foreground/60">حتى نعرض السعر والتوصيل الصحيح</span>
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-2.5">
              {countries.map((country) => (
                <Button
                  key={country.code}
                  variant={selectedCountry === country.code ? 'default' : 'outline'}
                  className="h-14 justify-start gap-2 rounded-2xl border-border/80 px-4 text-sm font-bold shadow-none"
                  onClick={() => handleSelectCountry(country.code)}
                >
                  <span className="text-xl">{country.flag}</span>
                  {country.name}
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Small button to show current country and change it
export const CountryIndicator = () => {
  const { getCountry } = useCountryStore();
  const [open, setOpen] = useState(false);
  const country = getCountry();

  return (
    <CountrySelector
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button aria-label="تغيير الدولة" className="flex items-center gap-1 rounded-xl p-2 transition-colors hover:bg-muted active:scale-95">
          <span className="text-lg">{country.flag}</span>
        </button>
      }
    />
  );
};
